import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faGlobe, faExclamationTriangle, faFolderOpen, faSave, faSearch } from "@fortawesome/pro-solid-svg-icons";
import { DateRangePicker, PDFExportOverlay } from "@perfectserve/library";
import ViewSettingsModal from "../components/ViewSettingsModal";
import KPISelectorModal from "../components/KPISelectorModal";
import PresentationModal from "../components/PresentationModal";
import PresetsSave from "../components/PresetsSave";
import PresetsOpen from "../components/PresetsOpen";
import { ContextSelector } from "@perfectserve/ps-context-selector";
import { Button } from "@perfectserve/library";
import { createDatePreset } from "../types/interactions";
import { useInteractionsData } from "../hooks/useInteractionsData";
import { useViewSettings } from "../hooks/useViewSettings";
import { resolveSelectedAccounts, resolveSelectedUsers, resolveDateRange, } from "../utils/selectionUtils";
import { saveCurrentSettings, loadSettings } from "../utils/settingsManagementUtils";
import { saveCurrentSession, restoreSession } from "../utils/sessionManagementUtils";
import { exportInteractionCardsToPDF } from "../utils/pdfExportUtils";
import { saveScrollPosition, restoreScrollPosition } from "../utils/scrollManagementUtils";
import { transitionToView, showPresetsWithTransition, hidePresetsWithTransition, } from "../utils/transitionUtils";
import { DEFAULT_CUSTOM_FILTERS } from "../utils/interactionsCardsConstants";
import { renderInteractionCard, createPresentationCards } from "../utils/interactionsCardsRenderUtils";
import { renderPlatformView, renderErrorsView, renderCallFinderView, renderInteractionsView, renderCallOutcomesView, renderExecutiveSummaryView, renderResponseMetricsView, } from "../utils/interactionsCardsViewUtils";
const InteractionsCards = ({ selectedAccounts: propSelectedAccounts, selectedUsers: propSelectedUsers, startDate: propStartDate, endDate: propEndDate, loading: propLoading = false, error: propError = null, title: _title = "Interactions Dashboard", showDatePicker = true, className = "", minimumCallDurationSeconds = 10, excludeHangupsAndDisconnects = true, onDateRangeChange, onCardClick: _onCardClick, }) => {
    var _a, _b;
    let isPerfectServeUser = false;
    try {
        const userContext = window.PSX_USER_CONFIGURATION;
        if (userContext === null || userContext === void 0 ? void 0 : userContext.permissions) {
            isPerfectServeUser = userContext.permissions.includes("role://PerfectServe Employee");
        }
    }
    catch (error) {
        isPerfectServeUser = false;
    }
    const [internalSelectedAccounts] = useState([]);
    const [internalSelectedUsers] = useState([]);
    const [internalDateRange, setInternalDateRange] = useState(() => createDatePreset("7d"));
    const [selectedSearchAccounts, setSelectedSearchAccounts] = useState([]);
    const [contextSelection, setContextSelection] = useState(() => ({
        accountIds: [],
        userIds: [],
        privilegeGroupIds: [],
        accounts: [],
        users: [],
        privilegeGroups: [],
    }));
    const [isPresentationOpen, setIsPresentationOpen] = useState(false);
    const [presentationCardIndex, setPresentationCardIndex] = useState(0);
    const [currentView, setCurrentView] = useState("dashboard");
    const [callOutcomesContext, setCallOutcomesContext] = useState(null);
    const [callFinderContext, setCallFinderContext] = useState(null);
    const [executiveContext, setExecutiveContext] = useState(null);
    const [responseMetricsContext, setResponseMetricsContext] = useState(null);
    const [dashboardScrollPosition, setDashboardScrollPosition] = useState(0);
    const [contentVisible, setContentVisible] = useState(true);
    const isStandaloneMode = useMemo(() => {
        return propSelectedAccounts === undefined && propSelectedUsers === undefined;
    }, [propSelectedAccounts, propSelectedUsers]);
    const selectedAccounts = useMemo(() => {
        return resolveSelectedAccounts(isStandaloneMode, contextSelection.accounts, selectedSearchAccounts, internalSelectedAccounts, propSelectedAccounts);
    }, [
        isStandaloneMode,
        contextSelection.accounts,
        selectedSearchAccounts,
        internalSelectedAccounts,
        propSelectedAccounts,
    ]);
    const selectedUsers = useMemo(() => {
        return resolveSelectedUsers(isStandaloneMode, contextSelection.users, internalSelectedUsers, propSelectedUsers);
    }, [isStandaloneMode, contextSelection.users, internalSelectedUsers, propSelectedUsers]);
    const dateRange = useMemo(() => {
        return resolveDateRange(isStandaloneMode, internalDateRange, propStartDate, propEndDate);
    }, [isStandaloneMode, internalDateRange, propStartDate, propEndDate]);
    const { viewSettings, updatePartialViewSettings, updateViewSettings } = useViewSettings();
    const [showViewSettingsDialog, setShowViewSettingsDialog] = useState(false);
    const hasInitializedDateRange = useRef(false);
    React.useEffect(() => {
        if (isStandaloneMode && viewSettings.defaultDateRange && !hasInitializedDateRange.current) {
            setInternalDateRange(createDatePreset(viewSettings.defaultDateRange));
            hasInitializedDateRange.current = true;
        }
    }, [viewSettings.defaultDateRange, isStandaloneMode]);
    const [showKPIModal, setShowKPIModal] = useState(false);
    const [currentSettingsName, setCurrentSettingsName] = useState("");
    const [showPresetsSave, setShowPresetsSave] = useState(false);
    const [presetsVisible, setPresetsVisible] = useState(true);
    const [dashboardVisible, setDashboardVisible] = useState(false);
    const presetsSaveButtonRef = useRef(null);
    const [isPdfExporting, setIsPdfExporting] = useState(false);
    const [pdfExportProgress, setPdfExportProgress] = useState(0);
    const [pdfExportStep, setPdfExportStep] = useState("");
    const pdfAbortControllerRef = useRef(null);
    const presetsVisibleRef = useRef(presetsVisible);
    const dashboardVisibleRef = useRef(dashboardVisible);
    presetsVisibleRef.current = presetsVisible;
    dashboardVisibleRef.current = dashboardVisible;
    const showPresetsWithTransitionCallback = useCallback(() => {
        showPresetsWithTransition({
            presetsVisible: presetsVisibleRef.current,
            dashboardVisible: dashboardVisibleRef.current,
            setDashboardVisible,
            setPresetsVisible
        });
    }, []);
    const hidePresetsWithTransitionCallback = useCallback(() => {
        hidePresetsWithTransition({
            presetsVisible: presetsVisibleRef.current,
            dashboardVisible: dashboardVisibleRef.current,
            setPresetsVisible,
            setDashboardVisible
        });
    }, []);
    useEffect(() => {
        const hasContext = selectedAccounts.length > 0 || selectedUsers.length > 0;
        if (hasContext) {
            setPresetsVisible(false);
            setDashboardVisible(true);
        }
        else {
            setPresetsVisible(true);
            setDashboardVisible(false);
        }
    }, []);
    const handleContextSelectionChange = useCallback((selection) => {
        setContextSelection(selection);
        setSelectedSearchAccounts([]);
        const hasContext = selection.accountIds.length > 0 || selection.userIds.length > 0;
        if (hasContext && presetsVisibleRef.current) {
            hidePresetsWithTransitionCallback();
        }
        else if (!hasContext && dashboardVisibleRef.current) {
            showPresetsWithTransitionCallback();
        }
    }, [hidePresetsWithTransitionCallback, showPresetsWithTransitionCallback]);
    const shouldAutoFetch = currentView === "dashboard";
    const { allInteractionsByAccount, allInteractions, loading: dataLoading, error: dataError, entityLoadingStates, } = useInteractionsData({
        dateRange,
        selectedAccounts,
        selectedUsers,
        viewSettings,
        autoFetch: shouldAutoFetch,
        minimumCallDurationSeconds,
        excludeHangupsAndDisconnects,
    });
    const loading = isStandaloneMode ? dataLoading : propLoading;
    const error = isStandaloneMode ? dataError : propError;
    const shouldShowAggregateCard = useMemo(() => {
        return (selectedAccounts.length > 1 ||
            selectedUsers.length > 1 ||
            (selectedAccounts.length > 0 && selectedUsers.length > 0));
    }, [selectedAccounts, selectedUsers]);
    const handleKPIChange = useCallback((kpi) => {
        updatePartialViewSettings({ selectedKPI: kpi });
    }, [updatePartialViewSettings]);
    const handleSaveKPI = useCallback((kpi) => {
        const existingIndex = viewSettings.savedKPIs.findIndex((k) => k.id === kpi.id);
        let updatedKPIs;
        if (existingIndex >= 0) {
            updatedKPIs = [...viewSettings.savedKPIs];
            updatedKPIs[existingIndex] = kpi;
        }
        else {
            updatedKPIs = [...viewSettings.savedKPIs, kpi];
        }
        updatePartialViewSettings({ savedKPIs: updatedKPIs });
    }, [viewSettings.savedKPIs, updatePartialViewSettings]);
    const handlePdfExport = useCallback(async () => {
        try {
            setIsPdfExporting(true);
            setPdfExportProgress(0);
            setPdfExportStep("Preparing PDF export...");
            const abortController = new AbortController();
            pdfAbortControllerRef.current = abortController;
            await exportInteractionCardsToPDF({
                selectedAccounts,
                selectedUsers,
                onProgress: (progress, step) => {
                    setPdfExportProgress(progress);
                    setPdfExportStep(step);
                },
                signal: abortController.signal,
            });
            setIsPdfExporting(false);
            setPdfExportProgress(0);
            setPdfExportStep("");
            pdfAbortControllerRef.current = null;
        }
        catch (error) {
            setIsPdfExporting(false);
            setPdfExportProgress(0);
            setPdfExportStep("");
            pdfAbortControllerRef.current = null;
            if (error instanceof Error && error.message === "Export cancelled") {
                console.log("PDF export cancelled by user");
                return;
            }
            if (error instanceof Error && error.message === "No cards found to export") {
                alert("No cards found to export");
                return;
            }
            console.error("PDF export failed:", error);
            alert("PDF export failed. Please try again.");
        }
    }, [selectedAccounts, selectedUsers]);
    const handlePdfExportCancel = useCallback(() => {
        if (pdfAbortControllerRef.current) {
            pdfAbortControllerRef.current.abort();
        }
        setIsPdfExporting(false);
        setPdfExportProgress(0);
        setPdfExportStep("");
        pdfAbortControllerRef.current = null;
    }, []);
    const handleDateRangeChange = useCallback((newRange) => {
        if (isStandaloneMode) {
            setInternalDateRange(newRange);
        }
        if (onDateRangeChange) {
            onDateRangeChange(newRange.startDate, newRange.endDate);
        }
    }, [isStandaloneMode, onDateRangeChange, updatePartialViewSettings]);
    const handleOpenViewSettings = useCallback(() => {
        setShowViewSettingsDialog(true);
    }, []);
    const handleCloseViewSettings = useCallback(() => {
        setShowViewSettingsDialog(false);
    }, []);
    const handleSaveViewSettings = useCallback((newSettings) => {
        updateViewSettings(newSettings);
        setShowViewSettingsDialog(false);
    }, [updateViewSettings]);
    const handleSaveCurrentSettings = useCallback((name) => {
        try {
            saveCurrentSettings(name, { dateRange, selectedAccounts, selectedUsers, viewSettings, contextSelection });
            setCurrentSettingsName(name);
        }
        catch (error) {
            alert(error instanceof Error ? error.message : "Failed to save preset. Please try again.");
        }
    }, [dateRange, selectedAccounts, selectedUsers, viewSettings, contextSelection]);
    const handleLoadSettings = useCallback((settings) => {
        try {
            loadSettings(settings, {
                dateRange,
                selectedAccounts,
                selectedUsers,
                viewSettings,
                contextSelection,
                isStandaloneMode,
                setInternalDateRange,
                setContextSelection,
                updatePartialViewSettings,
                hidePresetsWithTransition: hidePresetsWithTransitionCallback,
            });
            setCurrentSettingsName(settings.name);
        }
        catch (error) {
            alert(error instanceof Error ? error.message : "Failed to load preset. Please try again.");
        }
    }, [
        dateRange,
        selectedAccounts,
        selectedUsers,
        viewSettings,
        contextSelection,
        isStandaloneMode,
        setInternalDateRange,
        setContextSelection,
        updatePartialViewSettings,
        hidePresetsWithTransitionCallback,
    ]);
    const sessionDataRef = useRef({ viewSettings, isStandaloneMode, dateRange, selectedAccounts, selectedUsers, contextSelection });
    sessionDataRef.current = { viewSettings, isStandaloneMode, dateRange, selectedAccounts, selectedUsers, contextSelection };
    const saveCurrentSessionCallback = useCallback(() => {
        const { viewSettings, isStandaloneMode, dateRange, selectedAccounts, selectedUsers, contextSelection } = sessionDataRef.current;
        saveCurrentSession({
            viewSettings,
            isStandaloneMode,
            dateRange,
            selectedAccounts,
            selectedUsers,
            contextSelection,
        });
    }, []);
    const restoreSessionCallback = useCallback(() => {
        const { dateRange, selectedAccounts, selectedUsers, contextSelection, viewSettings, isStandaloneMode } = sessionDataRef.current;
        restoreSession({
            dateRange,
            selectedAccounts,
            selectedUsers,
            contextSelection,
            viewSettings,
            isStandaloneMode,
            setInternalDateRange,
            setContextSelection,
            updatePartialViewSettings,
            hidePresetsWithTransition: hidePresetsWithTransitionCallback,
        });
    }, [
        setInternalDateRange,
        setContextSelection,
        updatePartialViewSettings,
        hidePresetsWithTransitionCallback,
    ]);
    const hasRunSessionSaveRef = useRef(false);
    useEffect(() => {
        if (viewSettings.restoreLastSessionOnStartup && isStandaloneMode && !hasRunSessionSaveRef.current) {
            hasRunSessionSaveRef.current = true;
            const timeoutId = setTimeout(() => {
                saveCurrentSessionCallback();
            }, 1000);
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [viewSettings.restoreLastSessionOnStartup, isStandaloneMode, saveCurrentSessionCallback]);
    useEffect(() => {
        if (viewSettings.restoreLastSessionOnStartup) {
            restoreSessionCallback();
        }
    }, []);
    const getAggregateTitle = (selectedUsers, selectedAccounts) => {
        if (selectedUsers.length > 0 && selectedAccounts.length > 0) {
            return "All Selected Accounts & Users";
        }
        if (selectedUsers.length > 0) {
            return "All Selected Users";
        }
        return "All Selected Accounts";
    };
    const renderCard = (props) => {
        return renderInteractionCard(props, handleCallOutcomesView, handleExecutiveSummary, handleResponseMetricsView, handlePdfExport);
    };
    const presentationCards = useMemo(() => {
        return createPresentationCards({
            shouldShowAggregateCard,
            selectedAccounts,
            selectedUsers,
            allInteractions,
            allInteractionsByAccount,
            viewSettings,
            dateRange,
            entityLoadingStates,
            handleOpenInteractionsList,
            handleCallOutcomesView,
            handleExecutiveSummary,
            handleResponseMetricsView,
        });
    }, [
        shouldShowAggregateCard,
        selectedAccounts,
        selectedUsers,
        allInteractions,
        viewSettings,
        dateRange,
        allInteractionsByAccount,
        entityLoadingStates,
        handleOpenInteractionsList,
        handleCallOutcomesView,
        handleExecutiveSummary,
        handleResponseMetricsView,
    ]);
    const saveScrollPositionCallback = useCallback(() => {
        saveScrollPosition({ currentView, setDashboardScrollPosition });
    }, [currentView]);
    const dashboardScrollPositionRef = useRef(dashboardScrollPosition);
    dashboardScrollPositionRef.current = dashboardScrollPosition;
    const restoreScrollPositionCallback = useCallback(() => {
        restoreScrollPosition({ dashboardScrollPosition: dashboardScrollPositionRef.current, setContentVisible });
    }, []);
    const handleOpenPresentation = useCallback((cardIndex = 0) => {
        setPresentationCardIndex(cardIndex);
        setIsPresentationOpen(true);
    }, []);
    const handleClosePresentation = useCallback(() => {
        setIsPresentationOpen(false);
    }, []);
    const handlePresentationNavigate = useCallback((index) => {
        setPresentationCardIndex(index);
    }, []);
    const transitionToViewCallback = useCallback(async (newView, contextData) => {
        await transitionToView(newView, contextData, {
            setContentVisible,
            setCurrentView,
            setCallOutcomesContext,
            setCallFinderContext,
            setExecutiveContext,
            setResponseMetricsContext,
            restoreScrollPosition: restoreScrollPositionCallback,
        });
    }, [restoreScrollPositionCallback]);
    async function handleOpenInteractionsList() {
        saveScrollPositionCallback();
        await transitionToViewCallback("interactions");
    }
    const handleOpenPlatformMetrics = useCallback(async () => {
        saveScrollPositionCallback();
        await transitionToViewCallback("platform");
    }, [saveScrollPositionCallback, transitionToViewCallback]);
    const handleOpenNetworkErrors = useCallback(async () => {
        saveScrollPositionCallback();
        await transitionToViewCallback("errors");
    }, [saveScrollPositionCallback, transitionToViewCallback]);
    const handleOpenCallFinder = useCallback(async () => {
        saveScrollPositionCallback();
        await transitionToViewCallback("call-finder");
    }, [saveScrollPositionCallback, transitionToViewCallback]);
    const handleBackToDashboard = useCallback(async () => {
        saveScrollPositionCallback();
        await transitionToViewCallback("dashboard");
    }, [saveScrollPositionCallback, transitionToViewCallback]);
    async function handleCallOutcomesView(title, interactions, allInteractions) {
        saveScrollPositionCallback();
        await transitionToViewCallback("call-outcomes", { title, interactions, allInteractions });
    }
    async function handleExecutiveSummary(title, interactions, kpiResult, selectedAccountNames, selectedUserNames, viewSettings, summaryMetrics) {
        saveScrollPositionCallback();
        await transitionToViewCallback("executive-summary", {
            title,
            interactions,
            kpiResult,
            selectedAccountNames,
            selectedUserNames,
            viewSettings,
            summaryMetrics,
        });
    }
    async function handleResponseMetricsView(title, interactions, viewSettings, onInteractionClick) {
        saveScrollPositionCallback();
        await transitionToViewCallback("response-metrics", { title, interactions, viewSettings, onInteractionClick });
    }
    React.useEffect(() => {
        if (currentView === "dashboard" && dashboardScrollPosition > 0) {
            const timer = setTimeout(() => {
                restoreScrollPositionCallback();
            }, 100);
            return () => clearTimeout(timer);
        }
        else {
            setContentVisible(true);
        }
        return undefined;
    }, [currentView, dashboardScrollPosition, restoreScrollPositionCallback]);
    if (error) {
        return (_jsx("div", { className: `interactions-container ${className}`, children: _jsxs("div", { className: "interactions-error", children: [_jsx("h3", { children: "Error Loading Data" }), _jsx("p", { children: error })] }) }));
    }
    const renderMainContent = (forceStandaloneMode, includeHeader = true) => {
        var _a, _b;
        const modeIsStandalone = forceStandaloneMode !== undefined ? forceStandaloneMode : isStandaloneMode;
        return (_jsxs(_Fragment, { children: [includeHeader && (_jsx("div", { className: "interactions-header-background", children: _jsx("div", { className: "interactions-header", children: _jsx("div", { className: "interactions-controls d-flex align-items-center w-100", style: {
                                gap: "16px",
                                flexWrap: "nowrap",
                            }, children: modeIsStandalone && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "d-flex align-items-center", style: {
                                            gap: "8px",
                                            position: "relative",
                                        }, children: [_jsx(Button, { onClick: () => showPresetsWithTransitionCallback(), title: "Open Saved Preset", children: _jsx(FontAwesomeIcon, { icon: faFolderOpen }) }), _jsx("div", { ref: presetsSaveButtonRef, children: _jsx(Button, { onClick: () => setShowPresetsSave(true), title: "Save Current Preset", children: _jsx(FontAwesomeIcon, { icon: faSave }) }) })] }), _jsxs("div", { className: "d-flex align-items-center", style: {
                                            gap: "12px",
                                            flex: 1,
                                            justifyContent: "center",
                                        }, children: [_jsx("div", { style: { minWidth: "280px" }, children: _jsx(ContextSelector, { selectedAccountIds: contextSelection.accountIds, selectedUserIds: contextSelection.userIds, selectedPrivilegeGroupIds: contextSelection.privilegeGroupIds, selectedAccounts: contextSelection.accounts, selectedUsers: contextSelection.users, selectedPrivilegeGroups: contextSelection.privilegeGroups, onSelectionChange: handleContextSelectionChange }) }), showDatePicker && (_jsx("div", { style: { flexShrink: 0 }, children: _jsx(DateRangePicker, { startDate: dateRange.startDate, endDate: dateRange.endDate, onChange: handleDateRangeChange, disabled: false, defaultStartTime: (_a = dateRange.startTime) !== null && _a !== void 0 ? _a : null, defaultEndTime: (_b = dateRange.endTime) !== null && _b !== void 0 ? _b : null, preset: dateRange.preset || "custom", ...(dateRange.selectedDays && { selectedDays: dateRange.selectedDays }) }) }))] }), _jsxs("div", { className: "d-flex align-items-center", style: {
                                            gap: "8px",
                                        }, children: [_jsxs(Button, { onClick: handleOpenCallFinder, title: "Find Specific Calls", children: [_jsx(FontAwesomeIcon, { icon: faSearch }), "Call Finder"] }), _jsxs(Button, { onClick: handleOpenPlatformMetrics, title: "Platform Trends", children: [_jsx(FontAwesomeIcon, { icon: faGlobe }), "Platform"] }), isPerfectServeUser && (_jsxs(Button, { onClick: handleOpenNetworkErrors, title: "Error Investigation", children: [_jsx(FontAwesomeIcon, { icon: faExclamationTriangle }), "Errors"] })), _jsxs(Button, { onClick: handleOpenViewSettings, title: "Settings", children: [_jsx(FontAwesomeIcon, { icon: faCog }), "Settings"] })] })] })) }) }) })), _jsxs("div", { children: [selectedAccounts.length === 0 && selectedUsers.length === 0 && (_jsx("div", { className: "interactions-empty-state", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "mb-10", style: {
                                            fontSize: "18px",
                                            fontWeight: "500",
                                        }, children: modeIsStandalone ? "Select Context to Begin" : "No Data Available" }), _jsx("div", { style: { fontSize: "14px", lineHeight: "1.5" }, children: modeIsStandalone ? (_jsx("p", { children: "Choose accounts or users to view their interaction data." })) : (_jsx("p", { children: "No accounts or users provided for analysis." })) })] }) })), shouldShowAggregateCard &&
                            (selectedAccounts.length > 0 || selectedUsers.length > 0) &&
                            renderCard({
                                interactions: allInteractions,
                                title: getAggregateTitle(selectedUsers, selectedAccounts),
                                isAggregateCard: true,
                                isTopCard: true,
                                isLoading: Object.values(entityLoadingStates).some((loading) => loading),
                                showViewSettingsButton: !modeIsStandalone,
                                onViewSettingsClick: handleOpenViewSettings,
                                viewSettings: viewSettings,
                                dateRange: dateRange,
                                entityLoadingStates: entityLoadingStates,
                                selectedKPI: viewSettings.selectedKPI,
                                savedKPIs: viewSettings.savedKPIs,
                                onKPIChange: handleKPIChange,
                                onSaveKPI: handleSaveKPI,
                                onOpenKPIModal: () => setShowKPIModal(true),
                                selectedAccountNames: selectedAccounts.map((account) => account.name),
                                selectedUserNames: selectedUsers.map((user) => user.displayName || user.firstName + " " + user.lastName),
                                onPresentationClick: () => handleOpenPresentation(0),
                            }), selectedAccounts.map((account, accountIndex) => {
                            var _a;
                            const accountInteractions = allInteractionsByAccount[account.id] || null;
                            const accountTitle = accountInteractions && accountInteractions.length > 0 && ((_a = accountInteractions[0]) === null || _a === void 0 ? void 0 : _a.ToFilterObjectAccountName)
                                ? accountInteractions[0].ToFilterObjectAccountName
                                : account.name;
                            const presentationIndex = (shouldShowAggregateCard && (selectedAccounts.length > 0 || selectedUsers.length > 0) ? 1 : 0) +
                                accountIndex;
                            return renderCard({
                                key: account.id,
                                interactions: accountInteractions,
                                title: accountTitle,
                                isAggregateCard: false,
                                isTopCard: !shouldShowAggregateCard && accountIndex === 0,
                                showViewSettingsButton: !modeIsStandalone,
                                onViewSettingsClick: handleOpenViewSettings,
                                viewSettings: viewSettings,
                                dateRange: dateRange,
                                entityLoadingStates: entityLoadingStates,
                                entityId: account.id,
                                selectedKPI: viewSettings.selectedKPI,
                                savedKPIs: viewSettings.savedKPIs,
                                onKPIChange: handleKPIChange,
                                onSaveKPI: handleSaveKPI,
                                onOpenKPIModal: () => setShowKPIModal(true),
                                customFilters: DEFAULT_CUSTOM_FILTERS,
                                onPresentationClick: () => handleOpenPresentation(presentationIndex),
                            });
                        }), selectedUsers.map((user, userIndex) => {
                            var _a;
                            const userInteractions = allInteractionsByAccount[user.id] || null;
                            const userTitle = userInteractions && userInteractions.length > 0 && ((_a = userInteractions[0]) === null || _a === void 0 ? void 0 : _a.ToObjectName)
                                ? userInteractions[0].ToObjectName
                                : user.displayName;
                            const presentationIndex = (shouldShowAggregateCard && (selectedAccounts.length > 0 || selectedUsers.length > 0) ? 1 : 0) +
                                selectedAccounts.length +
                                userIndex;
                            return renderCard({
                                key: user.id,
                                interactions: userInteractions,
                                title: userTitle,
                                isAggregateCard: false,
                                isTopCard: !shouldShowAggregateCard && selectedAccounts.length === 0 && userIndex === 0,
                                showViewSettingsButton: !modeIsStandalone,
                                onViewSettingsClick: handleOpenViewSettings,
                                viewSettings: viewSettings,
                                dateRange: dateRange,
                                entityLoadingStates: entityLoadingStates,
                                entityId: user.id,
                                selectedKPI: viewSettings.selectedKPI,
                                savedKPIs: viewSettings.savedKPIs,
                                onKPIChange: handleKPIChange,
                                onSaveKPI: handleSaveKPI,
                                onOpenKPIModal: () => setShowKPIModal(true),
                                customFilters: DEFAULT_CUSTOM_FILTERS,
                                onPresentationClick: () => handleOpenPresentation(presentationIndex),
                            });
                        })] })] }));
    };
    if (currentView === "platform") {
        return renderPlatformView({ className, contentVisible, onClose: handleBackToDashboard });
    }
    if (currentView === "errors") {
        return renderErrorsView({ className, contentVisible, onClose: handleBackToDashboard });
    }
    if (currentView === "call-finder") {
        return renderCallFinderView({
            className,
            contentVisible,
            onClose: handleBackToDashboard,
            contextSelection,
            onContextSelectionChange: handleContextSelectionChange,
        });
    }
    if (currentView === "interactions") {
        return renderInteractionsView({
            className,
            contentVisible,
            onClose: handleBackToDashboard,
            allInteractions,
            loading,
        });
    }
    if (currentView === "call-outcomes" && callOutcomesContext) {
        return renderCallOutcomesView({
            className,
            contentVisible,
            onClose: handleBackToDashboard,
            callOutcomesContext,
            viewSettings,
        });
    }
    if (currentView === "executive-summary" && executiveContext) {
        return renderExecutiveSummaryView({ className, contentVisible, onClose: handleBackToDashboard, executiveContext });
    }
    if (currentView === "response-metrics" && responseMetricsContext) {
        return renderResponseMetricsView({
            className,
            contentVisible,
            onClose: handleBackToDashboard,
            responseMetricsContext,
        });
    }
    return (_jsxs("div", { className: `interactions-container ${className}`, children: [_jsx("div", { className: "interactions-header-background", children: _jsx("div", { className: "interactions-header", children: _jsxs("div", { className: "interactions-controls d-flex align-items-center w-100", style: {
                            gap: "16px",
                            flexWrap: "nowrap",
                        }, children: [_jsxs("div", { className: "d-flex align-items-center", style: {
                                    gap: "8px",
                                    position: "relative",
                                }, children: [_jsx(Button, { onClick: () => showPresetsWithTransitionCallback(), title: "Open Saved Preset", children: _jsx(FontAwesomeIcon, { icon: faFolderOpen }) }), _jsx("div", { ref: presetsSaveButtonRef, children: _jsx(Button, { onClick: () => setShowPresetsSave(true), title: "Save Current Preset", children: _jsx(FontAwesomeIcon, { icon: faSave }) }) })] }), _jsxs("div", { className: "d-flex align-items-center", style: {
                                    gap: "12px",
                                    flex: 1,
                                    justifyContent: "center",
                                }, children: [_jsx("div", { style: { minWidth: "280px" }, children: _jsx(ContextSelector, { selectedAccountIds: contextSelection.accountIds, selectedUserIds: contextSelection.userIds, selectedPrivilegeGroupIds: contextSelection.privilegeGroupIds, selectedAccounts: contextSelection.accounts, selectedUsers: contextSelection.users, selectedPrivilegeGroups: contextSelection.privilegeGroups, onSelectionChange: handleContextSelectionChange }) }), showDatePicker && (_jsx("div", { style: { flexShrink: 0 }, children: _jsx(DateRangePicker, { startDate: dateRange.startDate, endDate: dateRange.endDate, onChange: handleDateRangeChange, disabled: false, defaultStartTime: (_a = dateRange.startTime) !== null && _a !== void 0 ? _a : null, defaultEndTime: (_b = dateRange.endTime) !== null && _b !== void 0 ? _b : null, preset: dateRange.preset || "custom", ...(dateRange.selectedDays && { selectedDays: dateRange.selectedDays }) }) }))] }), _jsxs("div", { className: "d-flex align-items-center", style: {
                                    gap: "8px",
                                }, children: [_jsxs(Button, { onClick: handleOpenCallFinder, title: "Find Specific Calls", children: [_jsx(FontAwesomeIcon, { icon: faSearch }), "Call Finder"] }), _jsxs(Button, { onClick: handleOpenPlatformMetrics, title: "Platform Trends", children: [_jsx(FontAwesomeIcon, { icon: faGlobe }), "Platform"] }), isPerfectServeUser && (_jsxs(Button, { onClick: handleOpenNetworkErrors, title: "Error Investigation", children: [_jsx(FontAwesomeIcon, { icon: faExclamationTriangle }), "Errors"] })), _jsxs(Button, { onClick: handleOpenViewSettings, title: "Settings", children: [_jsx(FontAwesomeIcon, { icon: faCog }), "Settings"] })] })] }) }) }), _jsx("div", { style: {
                    opacity: presetsVisible ? 1 : 0,
                    transition: "opacity 0.3s ease-in-out",
                    position: presetsVisible ? "relative" : "absolute",
                    top: presetsVisible ? "auto" : 0,
                    left: presetsVisible ? "auto" : 0,
                    right: presetsVisible ? "auto" : 0,
                    zIndex: presetsVisible ? 1 : -1,
                }, children: _jsx(PresetsOpen, { isOpen: presetsVisible, onClose: hidePresetsWithTransitionCallback, onLoad: handleLoadSettings, hasSelectedContext: selectedAccounts.length > 0 || selectedUsers.length > 0 }) }), _jsx("div", { style: {
                    opacity: dashboardVisible && contentVisible ? 1 : 0,
                    transition: "opacity 0.3s ease-in-out",
                    position: dashboardVisible ? "relative" : "absolute",
                    top: dashboardVisible ? "auto" : 0,
                    left: dashboardVisible ? "auto" : 0,
                    right: dashboardVisible ? "auto" : 0,
                    zIndex: dashboardVisible ? 1 : -1,
                }, children: renderMainContent(undefined, false) }), _jsx(ViewSettingsModal, { isOpen: showViewSettingsDialog, viewSettings: viewSettings, onClose: handleCloseViewSettings, onSave: handleSaveViewSettings }), _jsx(KPISelectorModal, { isOpen: showKPIModal, onClose: () => setShowKPIModal(false), ...(viewSettings.selectedKPI && {
                    selectedKPI: viewSettings.selectedKPI,
                }), savedKPIs: viewSettings.savedKPIs, interactions: allInteractions, onKPIChange: handleKPIChange, onSaveKPI: handleSaveKPI }), _jsx(PresentationModal, { isOpen: isPresentationOpen, onClose: handleClosePresentation, cards: presentationCards, currentIndex: presentationCardIndex, onNavigate: handlePresentationNavigate }), _jsx(PresetsSave, { isOpen: showPresetsSave, onClose: () => setShowPresetsSave(false), onSave: handleSaveCurrentSettings, currentSettingsName: currentSettingsName, triggerRef: presetsSaveButtonRef }), _jsx(PDFExportOverlay, { isVisible: isPdfExporting, progress: pdfExportProgress, currentStep: pdfExportStep, onCancel: handlePdfExportCancel, canCancel: true })] }));
};
export default InteractionsCards;
//# sourceMappingURL=InteractionsCards.js.map